import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { Injector } from '@angular/core';
import { AuthfakeauthenticationService } from '../services/authfake.service';
import Swal from 'sweetalert2';

let isSessionExpiredFlag = false;

const RETRIED_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

const AUTH_ROUTES = [
  '/Authenticate/login',
  '/Authenticate/social',
  '/Authenticate/refresh',
  '/Authenticate/logout',
];

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const authFackservice = this.injector.get(AuthfakeauthenticationService);
    return next.handle(request).pipe(
      catchError((err) => {
        if (isSessionExpiredFlag) {
          return throwError(() => 'Sesión expirada');
        }

        const isDatacreditRequest = request.url.includes('apidtc.bakna.co');
        const isAuthRequest = AUTH_ROUTES.some((route) => request.url.includes(route));

        if (err.status === 401 && !isDatacreditRequest && !isAuthRequest) {
          if (
            authFackservice.hasRefreshToken() &&
            !request.context.get(RETRIED_AFTER_REFRESH)
          ) {
            return this.retryAfterRefresh(request, next, authFackservice);
          }

          this.notifySessionExpired(authFackservice);
          return throwError(() => 'Sesión expirada');
        }

        this.showError(err);

        const error = err.error?.message || err.statusText || 'Error';
        return throwError(() => error);
      }),
    );
  }

  private retryAfterRefresh(
    request: HttpRequest<any>,
    next: HttpHandler,
    authFackservice: AuthfakeauthenticationService,
  ): Observable<HttpEvent<any>> {
    return authFackservice.refreshSession().pipe(
      catchError((refreshErr) => {
        if (!authFackservice.hasRefreshToken()) {
          this.notifySessionExpired(authFackservice);
          return throwError(() => 'Sesión expirada');
        }

        this.showError(refreshErr);
        return throwError(() => 'No fue posible renovar la sesión');
      }),
      switchMap((resp: any) => {
        request.context.set(RETRIED_AFTER_REFRESH, true);

        return next
          .handle(
            request.clone({
              setHeaders: { Authorization: `Bearer ${resp.token}` },
            }),
          )
          .pipe(
            catchError((retryErr) => {
              if (retryErr.status === 401) {
                this.notifySessionExpired(authFackservice);
                return throwError(() => 'Sesión expirada');
              }

              this.showError(retryErr);
              return throwError(
                () => retryErr.error?.message || retryErr.statusText || 'Error',
              );
            }),
          );
      }),
    );
  }

  private notifySessionExpired(authFackservice: AuthfakeauthenticationService): void {
    if (isSessionExpiredFlag) return;
    isSessionExpiredFlag = true;

    Swal.fire({
      title: 'Sesión expirada',
      text: 'Su sesión ha expirado. Por favor, vuelva a iniciar sesión.',
      icon: 'warning',
      confirmButtonColor: '#34c38f',
      confirmButtonText: 'Ir al Login',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      isSessionExpiredFlag = false;
      authFackservice.logout();
      location.reload();
    });
  }

  private showError(err: any): void {
    console.log(err);

    if (err.status === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin conexión',
        text: 'No pudimos comunicarnos con el servidor. Revise su conexión e intente de nuevo.',
        confirmButtonColor: '#34c38f',
      });
      return;
    }

    const message =
      err.error?.message ||
      err.error?.data ||
      err.message ||
      err.statusText ||
      'Error desconocido';

    const messageText = String(message);

    if (messageText.includes('No existe histórico para el documento indicado')) {
      Swal.fire({
        icon: 'warning',
        title: 'Información Pendiente',
        text: 'Actualmente no se encuentran datos registrados para este documento. Por favor, haga clic en el botón "Consultar Data Credito" para obtener la información.',
        confirmButtonColor: '#34c38f',
      });
    } else if (messageText.includes('Checkout already exists')) {
      Swal.fire({
        icon: 'info',
        text: 'Existe un proceso de pago pendiente, por favor, espere que finalice el pago.',
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        html: err.error?.error
          ? err.error.error
          : `Actualmente no puedes ingresar a este módulo. <br> Código Error: ${messageText}`,
      });
    }
  }
}
